import clsx from "clsx";
import { PointerEvent, useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import {
  fieldFallbackLabel,
  getUITranslation,
} from "@smart/bridge-intake-components-dom";
import { useAsync } from "@smart/itops-hooks-dom";
import {
  Badge,
  Button,
  ContextMenu,
  DragHandleProps,
  Icon,
  Modal,
  Tooltip,
} from "@smart/itops-sb-design-system-dom";
import { delay } from "@smart/itops-utils-basic";

import {
  getGroupCardClassNames,
  getGroupFieldContainerClassNames,
  getGroupFieldListClassNames,
  groupStyles,
} from "./class-names";
import { LanguageKey, baseLanguageKey } from "../../../../types";
import { DroppableSeparator } from "../../drag-and-drop";
import { ConditionsButton } from "../../edit-view/condition/conditions-button";
import { EditableText } from "../../editor/editable-text";
import {
  ConditionActions,
  FieldTemplate,
  GqlField,
  GqlFieldValues,
  GqlGroup,
  GqlGroupValues,
  GqlMatterLayout,
  GqlMatterType,
  LoadMatterFields,
  OnDeleteField,
  OnFieldTypeChange,
  OnUpdateField,
  QuestionEditMode,
} from "../../types";
import {
  ChangeStatus,
  ContactType,
  EntityChangesMap,
  changeStatusLabel,
  findRoleContactFields,
  getContactDetailsType,
  getContactRoleLayouts,
  hasExistingContactDetailsGroup,
  isChanged,
  isContactDetailsGroup as checkIsContactDetailsGroup,
} from "../../utils";
import { Field } from "../field";
import { RemovedFieldCard } from "../removed-field-card";

const duplicateRoleWarning = "Duplicate field. Remove to select.";

const findBlockedRoleLayoutIds = ({
  roleLayouts,
  contactType,
  currentLayoutId,
  groupUri,
  formFields,
  formGroups,
}: {
  roleLayouts: GqlMatterLayout[];
  contactType: ContactType;
  currentLayoutId: string | undefined;
  groupUri: string;
  formFields: GqlField[];
  formGroups: GqlGroup[];
}): Set<string> =>
  new Set(
    roleLayouts
      .filter(
        (layout) =>
          layout.id !== currentLayoutId &&
          (hasExistingContactDetailsGroup({
            groups: formGroups,
            layoutId: layout.id,
            contactType,
            excludeGroupUri: groupUri,
          }) ||
            findRoleContactFields({
              fields: formFields,
              layoutId: layout.id,
              contactType,
              excludeGroupUri: groupUri,
            }).length > 0),
      )
      .map((layout) => layout.id),
  );

type GroupCardProps = {
  group: GqlGroupValues;
  groupFields?: GqlFieldValues[] | null;
  isDragging?: boolean;
  onDeleteGroup: () => Promise<void>;
  onDuplicateGroup: () => Promise<void>;
  onUpdateGroup: ({
    label,
    description,
    repeatable,
    repeatPrompt,
  }: {
    label?: string;
    description?: string;
    repeatable?: boolean;
    repeatPrompt?: string;
  }) => Promise<void>;
  onChangeContactRole?: (roleLayout: GqlMatterLayout) => Promise<void>;
  mode?: QuestionEditMode;
  editing?: boolean;
  loading?: boolean;
  onUpdateField: OnUpdateField;
  onDeleteField: OnDeleteField;
  updatingItems: Record<string, boolean | undefined>;
  editingItemUri?: string;
  matterLayouts?: GqlMatterLayout[];
  matterTypes?: GqlMatterType[];
  loadMatterFields: LoadMatterFields;
  dragHandleProps?: DragHandleProps | null;
  conditionActions: ConditionActions;
  onFieldTypeChange: OnFieldTypeChange;
  excludingFieldTemplates: FieldTemplate[];
  className?: string;
  lastActiveIds: string[];
  groupContainsPaymentField?: boolean;
  viewingLanguage: LanguageKey;
  hasSubmissionLinks?: boolean;
  fieldChangesMap?: EntityChangesMap;
  changeStatus?: ChangeStatus;
  onAddAndRemoveContactFields?: () => void;
  formFields: GqlField[];
  formGroups: GqlGroup[];
};

export const GroupCard = ({
  mode,
  group,
  onDeleteGroup,
  editing,
  onUpdateGroup,
  onDuplicateGroup,
  onChangeContactRole,
  groupFields,
  updatingItems,
  onUpdateField,
  onDeleteField,
  editingItemUri,
  matterLayouts,
  matterTypes,
  loadMatterFields,
  isDragging: isDraggingFromSortable,
  dragHandleProps,
  conditionActions,
  onFieldTypeChange,
  excludingFieldTemplates,
  className,
  lastActiveIds,
  groupContainsPaymentField,
  viewingLanguage,
  hasSubmissionLinks,
  fieldChangesMap,
  changeStatus,
  onAddAndRemoveContactFields,
  formFields,
  formGroups,
}: GroupCardProps) => {
  const {
    uri: groupUri,
    label: groupTitle = "",
    description,
    repeatable,
    field: groupMatterField,
    layout: groupLayout,
    repeatPrompt,
    translations,
    type: groupType,
  } = group;
  const isContactDetailsGroup = checkIsContactDetailsGroup({ type: groupType });
  const contactDetailsType = getContactDetailsType(groupMatterField);
  // memoize for identity, a fresh array retriggers the check below
  const roleLayouts = useMemo(
    () =>
      isContactDetailsGroup ? getContactRoleLayouts(matterLayouts || []) : [],
    [isContactDetailsGroup, matterLayouts],
  );
  const currentRoleLabel = groupLayout?.displayName || groupLayout?.name || "";
  const contactTypeIcon =
    contactDetailsType === "company"
      ? "regularBuildingColumns"
      : "regularCircleUser";
  const contactTypeLabelMap = {
    company: "Company",
    person: "Person",
  };
  const contactTypeLabel = contactDetailsType
    ? contactTypeLabelMap[contactDetailsType]
    : undefined;
  const groupTranslation =
    viewingLanguage !== baseLanguageKey
      ? translations?.find(
          (translation) => translation.language === viewingLanguage,
        )
      : undefined;
  const translatedLabel = groupTranslation?.label;
  const translatedDescription = groupTranslation?.description;
  const translatedRepeatPrompt = groupTranslation?.repeatPrompt;
  const isViewingBaseLanguage = viewingLanguage === baseLanguageKey;
  const defaultAddButtonLabel =
    getUITranslation("add", viewingLanguage) || "Add";
  const [isEditingTitle, setIsEditingTitle] = useState(editing);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingRepeatPrompt, setIsEditingRepeatPrompt] = useState(false);
  const [isDragging, setIsDragging] = useState(isDraggingFromSortable);
  const onPointerDownEventRef = useRef<
    PointerEvent<HTMLDivElement> | undefined
  >(undefined);
  const [isDescriptionVisible, setIsDescriptionVisible] = useState(
    Boolean(translatedDescription ?? description),
  );
  const [editedGroupTitle, setEditedGroupTitle] = useState(
    translatedLabel || groupTitle || fieldFallbackLabel.group,
  );
  const [editedDescription, setEditedDescription] = useState<
    string | undefined
  >(translatedDescription || description || "");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editedRepeatPrompt, setEditedRepeatPrompt] = useState(
    translatedRepeatPrompt || repeatPrompt || defaultAddButtonLabel,
  );

  useEffect(() => {
    setIsEditingTitle(editing);
  }, [editing]);

  useEffect(() => {
    setEditedGroupTitle(
      translatedLabel || groupTitle || fieldFallbackLabel.group,
    );
    setEditedDescription(translatedDescription || description || "");
    setIsDescriptionVisible(Boolean(translatedDescription ?? description));
  }, [translatedLabel, translatedDescription, groupTitle, description]);

  useAsync(async () => {
    if (isDragging && onPointerDownEventRef.current) {
      // This is to make sure the shrinking happens before the dragging starts
      await delay(300);

      // Check again in case the user releases the mouse during the delay
      if (onPointerDownEventRef.current) {
        dragHandleProps?.listeners?.onPointerDown?.(
          onPointerDownEventRef.current,
        );
      }
    } else {
      onPointerDownEventRef.current = undefined;
    }
  }, [isDragging]);

  useEffect(() => {
    if (!isDraggingFromSortable) {
      setIsDragging(false);
    }
  }, [isDraggingFromSortable]);

  const handleSaveLabel = async (value: string) => {
    setEditedGroupTitle(value);
    await onUpdateGroup({ label: value, description: editedDescription });
  };

  const handleSaveDescription = async (value: string) => {
    setEditedDescription(value);
    await onUpdateGroup({ label: editedGroupTitle, description: value });
  };

  const handleSaveRepeatPrompt = async (value: string) => {
    setEditedRepeatPrompt(value);
    await onUpdateGroup({
      repeatPrompt: value,
      label: editedGroupTitle,
      description: editedDescription,
    });
  };

  const blockedRoleLayoutIds = useMemo(
    () =>
      isContactDetailsGroup && isViewingBaseLanguage && contactDetailsType
        ? findBlockedRoleLayoutIds({
            roleLayouts,
            contactType: contactDetailsType,
            currentLayoutId: groupLayout?.id,
            groupUri,
            formFields,
            formGroups,
          })
        : new Set<string>(),
    [
      isContactDetailsGroup,
      isViewingBaseLanguage,
      contactDetailsType,
      roleLayouts,
      groupLayout?.id,
      groupUri,
      formFields,
      formGroups,
    ],
  );

  return (
    <>
      <div
        className={clsx(
          getGroupCardClassNames({
            current: !!isDragging || lastActiveIds.includes(groupUri),
          }),
          "relative",
          className,
        )}
      >
        {isChanged(changeStatus) && (
          <div
            data-testid="change-indicator"
            className="absolute -left-[0.2rem] -top-[0.2rem] -bottom-[0.2rem] w-[0.4rem] bg-blue-460 rounded-l"
          />
        )}
        <div
          className={clsx(
            groupStyles.dragHandle,
            isDragging && "cursor-grab",
            !isViewingBaseLanguage &&
              "cursor-not-allowed opacity-50 hover:bg-transparent",
          )}
          {...(isViewingBaseLanguage
            ? {
                ...dragHandleProps?.attributes,
                ...dragHandleProps?.listeners,
                ...dragHandleProps?.listeners,
              }
            : {})}
          onPointerDown={(event) => {
            if (!isViewingBaseLanguage) return;
            setIsDragging(true);
            onPointerDownEventRef.current = event;
          }}
          onPointerUp={() => {
            if (!isViewingBaseLanguage) return;
            setIsDragging(false);
            onPointerDownEventRef.current = undefined;
          }}
          tabIndex={0}
          role="button"
        >
          <Icon name="solidGripDotsVertical" className="w-8 h-8 font-bold" />
        </div>
        <div className="flex flex-col justify-center w-full pb-[1.2rem] min-w-0">
          <div className={groupStyles.header}>
            <div className={groupStyles.title}>
              <div className="flex items-start gap-small">
                <EditableText
                  text={editedGroupTitle}
                  onChange={setEditedGroupTitle}
                  onComplete={() =>
                    onUpdateGroup({
                      label: editedGroupTitle,
                      description: editedDescription,
                    })
                  }
                  onSave={handleSaveLabel}
                  editing={isEditingTitle}
                  onEditing={setIsEditingTitle}
                  fontSize="paragraph"
                  fontWeight="semibold"
                  isEditor
                />
                {isChanged(changeStatus) && (
                  <Badge
                    className="mt-[0.4rem]"
                    label={changeStatusLabel[changeStatus]}
                    theme="information"
                  />
                )}
              </div>
            </div>
            <div className={groupStyles.groupActions}>
              {conditionActions.checkHasConditions(group.uri) && (
                <ConditionsButton
                  dataTestId="group-conditions-button"
                  numberOfConditions={conditionActions.getNumberOfConditions(
                    group.uri,
                  )}
                  onClick={() => conditionActions.setEditingCondition(group)}
                  size="small"
                  disabled={!isViewingBaseLanguage}
                />
              )}
              <Tooltip
                title="Repeat fields"
                text={
                  groupContainsPaymentField
                    ? "This group can't be made repeatable because it contains Smokeball Payments. Remove the component to continue."
                    : "Allows clients to enter the same information for multiple entities (e.g., adding details for multiple children, properties, etc.).\n\nWhen enabled, an 'Add' button will appear in this section, giving your client the flexibility to include as many entries as needed."
                }
                placement="top-end"
                offsetBy={7}
                variant="paragraphy"
              >
                <Button
                  dataTestId="repeat-group-button"
                  leftIcon="regularArrowsRepeat"
                  variant="secondarySubtle"
                  onClick={async () =>
                    onUpdateGroup({ repeatable: !repeatable })
                  }
                  isPressed={!!repeatable}
                  disabled={groupContainsPaymentField || !isViewingBaseLanguage}
                  size="small"
                />
              </Tooltip>
              <Tooltip
                title="Description text"
                text="Add a description to guide clients on what to enter."
                placement="top-end"
                offsetBy={7}
                variant="paragraphy"
              >
                <Button
                  dataTestId="group-description-button"
                  leftIcon="regularText"
                  variant="secondarySubtle"
                  onClick={async () => {
                    setIsDescriptionVisible(!isDescriptionVisible);
                    if (isDescriptionVisible) {
                      setEditedDescription("");
                      await onUpdateGroup({ description: "" });
                    }
                  }}
                  isPressed={isDescriptionVisible}
                  disabled={!isViewingBaseLanguage}
                  size="small"
                />
              </Tooltip>
              {!isContactDetailsGroup && (
                <Tooltip
                  title="Duplicate Group"
                  text="Create a duplicate of this group to speed up form creation. Ensure you remap fields and conditions to finalise the setup."
                  placement="top-end"
                  offsetBy={7}
                  variant="paragraphy"
                >
                  <Button
                    variant="secondarySubtle"
                    leftIcon="regularCopy"
                    dataTestId={`duplicate-group-button-${group.uri}`}
                    onClick={async () => {
                      await onDuplicateGroup();
                    }}
                    disabled={!isViewingBaseLanguage}
                  />
                </Tooltip>
              )}
              <Tooltip
                title="Delete group"
                text="Click to delete group"
                placement="top-end"
                offsetBy={7}
                variant="paragraphy"
              >
                <Button
                  dataTestId="delete-group-button"
                  leftIcon="regularTrashCan"
                  variant="secondarySubtle"
                  onClick={() => setIsDeleteModalOpen(true)}
                  size="small"
                  disabled={!isViewingBaseLanguage}
                />
              </Tooltip>
            </div>
          </div>
          {isDescriptionVisible && (
            <div className="mb-[0.8rem]">
              <EditableText
                editing={isEditingDescription}
                text={editedDescription}
                onChange={setEditedDescription}
                onComplete={async () => {
                  setIsEditingDescription(false);
                  await onUpdateGroup({
                    label: editedGroupTitle,
                    description: editedDescription,
                  });
                }}
                onSave={handleSaveDescription}
                placeholder="Add description text to guide clients on what to enter. This won't show on the final form submission."
                fontSize="paragraph"
                isEditor
              />
            </div>
          )}
          {isContactDetailsGroup && (
            <div
              className="flex items-center gap-small mb-[0.8rem] text-ui"
              data-testid="contact-details-applies-to"
            >
              <span>Applies to</span>
              <ContextMenu
                button={{
                  text: currentRoleLabel,
                  leftIcon: contactTypeIcon,
                  rightIcon: "regularAngleDown",
                  variant: "secondarySubtle",
                  size: "small",
                  disabled: !isViewingBaseLanguage,
                  dataTestId: "contact-details-role-select",
                }}
                items={roleLayouts.map((layout) => {
                  const isBlocked = blockedRoleLayoutIds.has(layout.id);

                  return {
                    id: `contact-details-role-${layout.id}`,
                    label: layout.displayName || layout.name,
                    // outside the button so the tooltip opens while disabled
                    disabled: isBlocked,
                    warningTooltip: isBlocked
                      ? duplicateRoleWarning
                      : undefined,
                    onClick: () => onChangeContactRole?.(layout),
                  };
                })}
              />
              {contactTypeLabel && (
                <>
                  <span aria-hidden="true">·</span>
                  <span data-testid="contact-details-type-chip">
                    {contactTypeLabel}
                  </span>
                </>
              )}
            </div>
          )}
          <div
            className={clsx(
              groupStyles.content,
              repeatable ? "pb-[2.8rem] mb-[2.8rem]" : "pb-8 mb-[1.2rem]",
            )}
          >
            <div
              className={getGroupFieldListClassNames({
                shrinking: isDragging,
              })}
              data-group-field-list={groupUri}
            >
              <DroppableSeparator
                index={0}
                groupUri={groupUri}
                variant={groupFields?.length ? "field" : "initial-group"}
                disabled={mode === "reordering" || isContactDetailsGroup}
              />
              {groupFields?.length ? (
                groupFields.map((groupField, i) => (
                  <div
                    className={twMerge(
                      getGroupFieldContainerClassNames({
                        shrinking: isDragging,
                        index: i,
                      }),
                    )}
                    key={groupField.uri}
                    data-testid="group-field-container"
                  >
                    {fieldChangesMap?.get(groupField.uri) === "removed" ? (
                      <RemovedFieldCard field={groupField} />
                    ) : (
                      <Field
                        index={i}
                        item={groupField}
                        loading={updatingItems[groupField.uri]}
                        shrinking={isDragging}
                        mode={mode}
                        onUpdateField={onUpdateField}
                        onDeleteField={onDeleteField}
                        isEditing={groupField.uri === editingItemUri}
                        matterLayouts={matterLayouts}
                        matterTypes={matterTypes}
                        loadMatterFields={loadMatterFields}
                        conditionActions={conditionActions}
                        groupLayout={groupLayout}
                        groupMatterField={groupMatterField}
                        onFieldTypeChange={onFieldTypeChange}
                        excludingFieldTemplates={excludingFieldTemplates}
                        lastActiveIds={lastActiveIds}
                        viewingLanguage={viewingLanguage}
                        hasSubmissionLinks={hasSubmissionLinks}
                        changeStatus={fieldChangesMap?.get(groupField.uri)}
                        disableDrop={isContactDetailsGroup}
                        allowChangeFieldType={!isContactDetailsGroup}
                      />
                    )}
                  </div>
                ))
              ) : (
                <div className="group-field-spacer" />
              )}
              {isDragging && <div className={groupStyles.shrinkShade} />}
            </div>
            {!!repeatable && (
              <div className="relative max-w-[80rem] w-full m-auto">
                <div className={groupStyles.repeatButton}>
                  <Icon name="regularPlus" />
                  <EditableText
                    editing={isEditingRepeatPrompt}
                    text={editedRepeatPrompt}
                    onChange={setEditedRepeatPrompt}
                    onComplete={async () => {
                      setIsEditingRepeatPrompt(false);
                      await onUpdateGroup({
                        repeatPrompt: editedRepeatPrompt,
                        label: editedGroupTitle,
                        description: editedDescription,
                      });
                    }}
                    onSave={handleSaveRepeatPrompt}
                    placeholder={defaultAddButtonLabel}
                  />
                </div>
              </div>
            )}
          </div>
          {isContactDetailsGroup && (
            <Button
              text="Add and remove fields"
              leftIcon="regularPlus"
              variant="primarySubtle"
              size="small"
              className="mt-[0.8rem] self-start border border-dashed border-blue-240"
              dataTestId="contact-details-add-and-remove-fields"
              disabled={
                !isViewingBaseLanguage || !groupLayout || !contactDetailsType
              }
              onClick={onAddAndRemoveContactFields}
            />
          )}
        </div>
      </div>
      <Modal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        header={{
          icon: "solidTrashCan",
          text: "Delete group",
        }}
        footer={{
          buttons: [
            {
              variant: "secondarySubtle",
              text: "Cancel",
              onClick: () => setIsDeleteModalOpen(false),
            },
            {
              variant: "primaryDefault",
              text: "Yes, delete",
              onClick: async () => {
                setIsDeleteModalOpen(false);
                await onDeleteGroup();
              },
            },
          ],
        }}
      >
        Are you sure you want to delete the group?
      </Modal>
    </>
  );
};
